/**
 * Minimal browser → Microsoft Graph client using a delegated access token.
 * Never send Graph tokens to this app's Fastify API.
 */

import { captureSession } from '@cdf/api-client-react';
import type { GraphAccess } from './auth-config';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

export type GraphErrorCode =
  | 'auth'
  | 'permission'
  | 'throttle'
  | 'not_found'
  | 'places_unavailable'
  | 'validation'
  | 'invalid_response'
  | 'unknown';

export class GraphClientError extends Error {
  readonly status: number;
  readonly code: GraphErrorCode;
  readonly graphCode?: string;
  readonly retryAfterMs?: number;

  constructor(
    message: string,
    options: {
      status: number;
      code: GraphErrorCode;
      graphCode?: string;
      retryAfterMs?: number;
    }
  ) {
    super(message);
    this.name = 'GraphClientError';
    this.status = options.status;
    this.code = options.code;
    this.graphCode = options.graphCode;
    this.retryAfterMs = options.retryAfterMs;
  }
}

export interface GraphODataCollection<T> {
  value: T[];
  '@odata.nextLink'?: string;
  '@odata.count'?: number;
}

export interface DirectoryUser {
  id: string;
  displayName: string;
  email: string;
}

/** Bounded, delegated directory suggestions for administrator invite pickers. */
export async function searchDirectoryUsers(input: string): Promise<DirectoryUser[]> {
  const term = input.trim();
  if (term.length < 2 || term.length > 100) return [];
  const escaped = term.replace(/'/g, "''");
  const query = new URLSearchParams({
    '$filter': `startswith(displayName,'${escaped}') or startswith(mail,'${escaped}')`,
    '$select': 'id,displayName,mail,userPrincipalName',
    '$count': 'true',
    '$top': '8'
  });
  const result = await graphFetch<GraphODataCollection<{
    id?: unknown; displayName?: unknown; mail?: unknown; userPrincipalName?: unknown;
  }>>(`/users?${query.toString()}`, { headers: { ConsistencyLevel: 'eventual' } }, undefined, 'directory');
  if (!Array.isArray(result.value)) {
    throw new GraphClientError('Microsoft Graph returned an invalid directory result.', {
      status: 0, code: 'invalid_response'
    });
  }
  return result.value.flatMap((entry) => {
    const email = typeof entry.mail === 'string' && entry.mail.includes('@')
      ? entry.mail.trim() : typeof entry.userPrincipalName === 'string' && !entry.userPrincipalName.includes('#EXT#')
        ? entry.userPrincipalName.trim() : '';
    if (typeof entry.id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(entry.id)
      || typeof entry.displayName !== 'string' || !entry.displayName.trim()
      || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return [];
    return [{ id: entry.id.toLowerCase(), displayName: entry.displayName.trim(), email }];
  });
}

/**
 * Exact, administrator-initiated lookup. An ambiguous or incomplete directory
 * result cannot become a platform grant.
 */
export async function findDirectoryUserByEmail(input: string): Promise<DirectoryUser> {
  const email = input.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 320) {
    throw new GraphClientError('Enter a valid directory email address.', { status: 0, code: 'validation' });
  }
  const escaped = email.replace(/'/g, "''");
  const query = new URLSearchParams({
    '$filter': `mail eq '${escaped}' or userPrincipalName eq '${escaped}'`,
    '$select': 'id,displayName,mail,userPrincipalName',
    '$top': '25'
  });
  const result = await graphFetch<GraphODataCollection<{
    id?: unknown;
    displayName?: unknown;
    mail?: unknown;
    userPrincipalName?: unknown;
  }>>(`/users?${query.toString()}`, {}, undefined, 'directory');
  if (!Array.isArray(result.value) || result['@odata.nextLink']) {
    throw new GraphClientError('Directory search was incomplete. Ask an identity administrator to resolve this user.', {
      status: 0, code: 'invalid_response'
    });
  }
  const matches = result.value.filter((entry) =>
    (typeof entry.mail === 'string' && entry.mail.toLowerCase() === email)
    || (typeof entry.userPrincipalName === 'string' && entry.userPrincipalName.toLowerCase() === email));
  if (matches.length === 0) {
    throw new GraphClientError('No matching user was found in this Entra directory.', { status: 404, code: 'not_found' });
  }
  const unique = new Map<string, typeof matches[number]>();
  for (const entry of matches) {
    if (typeof entry.id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(entry.id)
      || typeof entry.displayName !== 'string' || !entry.displayName.trim()) {
      throw new GraphClientError('The directory returned an incomplete identity.', { status: 0, code: 'invalid_response' });
    }
    unique.set(entry.id.toLowerCase(), entry);
  }
  if (unique.size !== 1) {
    throw new GraphClientError('More than one directory user matches this address. No access was granted.', {
      status: 0, code: 'invalid_response'
    });
  }
  const selected = [...unique.values()][0]!;
  return {
    id: (selected.id as string).toLowerCase(),
    displayName: (selected.displayName as string).trim(),
    email
  };
}

export type TeamsPresence = {
  availability: string;
  activity: string;
  fetchedAt: string;
};

/** Delegated, consent-gated Teams signal. Caller must first receive a privacy-approved user ID. */
export async function getTeamsPresence(userId: string, scope?: { signal?: AbortSignal; assertContext?: () => void }): Promise<TeamsPresence> {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId)) {
    throw new GraphClientError('Invalid directory user ID.', { status: 0, code: 'validation' });
  }
  const response = await graphFetch<GraphODataCollection<{
    id?: string; availability?: string; activity?: string;
  }>>('/communications/getPresencesByUserId', {
    method: 'POST',
    body: JSON.stringify({ ids: [userId] }), signal: scope?.signal
  }, undefined, 'presence', scope?.assertContext);
  const row = response.value?.find((item) => item.id?.toLowerCase() === userId.toLowerCase());
  if (!row || !row.availability || row.availability === 'presenceUnknown') throw new GraphClientError('Teams availability is unavailable for this colleague.',
    { status: 0, code: 'invalid_response' });
  return { availability: row.availability || 'presenceUnknown',
    activity: row.activity || 'presenceUnknown', fetchedAt: new Date().toISOString() };
}

/** Exchange place room (subset used by Rooms & Resources). */
export interface GraphRoom {
  id: string;
  displayName?: string | null;
  emailAddress?: string | null;
  /** Microsoft Places room tags (e.g. Focus, Quiet) → spaces.tags via merge helper. */
  tags?: string[] | null;
  capacity?: number | null;
  building?: string | null;
  floorLabel?: string | null;
  floorNumber?: number | null;
  audioDeviceName?: string | null;
  videoDeviceName?: string | null;
  displayDeviceName?: string | null;
  isWheelChairAccessible?: boolean | null;
  phone?: string | null;
  nickname?: string | null;
  bookingType?: string | null;
  label?: string | null;
}

export interface GraphRoomList {
  id: string;
  displayName?: string | null;
  emailAddress?: string | null;
  phone?: string | null;
  address?: {
    city?: string | null;
    countryOrRegion?: string | null;
    postalCode?: string | null;
    state?: string | null;
    street?: string | null;
  } | null;
}

type TokenProvider = (access: GraphAccess) => Promise<string | null>;

let graphTokenProvider: TokenProvider | null = null;

/** Register the MSAL Graph token getter once (from AuthProvider). */
export function setGraphTokenProvider(provider: TokenProvider | null): void {
  graphTokenProvider = provider;
}

function classifyStatus(status: number, access: GraphAccess): GraphErrorCode {
  if (status === 401) return 'auth';
  if (status === 429) return 'throttle';
  if (status === 404) return 'not_found';
  if (status === 403) return access === 'places' ? 'places_unavailable' : 'permission';
  return 'unknown';
}

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.round(seconds * 1000);
  const date = Date.parse(header);
  if (!Number.isNaN(date)) return Math.max(0, date - Date.now());
  return undefined;
}

function isAllowedGraphHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'graph.microsoft.com';
}

function resolveUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('https://') || pathOrUrl.startsWith('http://')) {
    let parsed: URL;
    try {
      parsed = new URL(pathOrUrl);
    } catch {
      throw new GraphClientError('Invalid Microsoft Graph URL.', {
        status: 0,
        code: 'unknown'
      });
    }
    if (parsed.protocol !== 'https:' || !isAllowedGraphHost(parsed.hostname)) {
      throw new GraphClientError(
        'Refusing to call a non-Graph host with a Microsoft Graph access token.',
        { status: 0, code: 'unknown' }
      );
    }
    return parsed.toString();
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${GRAPH_BASE}${path}`;
}

export async function graphFetch<T>(
  pathOrUrl: string,
  init: RequestInit = {},
  tokenOverride?: string | null,
  access: GraphAccess = 'calendar',
  assertContext?: () => void
): Promise<T> {
  const session = captureSession(init.signal);
  const check = () => { session.assertCurrent(); assertContext?.(); };
  check();
  const token =
    tokenOverride === undefined
      ? graphTokenProvider
        ? await graphTokenProvider(access)
        : null
      : tokenOverride;
  check();

  if (!token) {
    throw new GraphClientError(
      'Microsoft Graph permission is required. Connect the requested directory or calendar access and try again.',
      { status: 401, code: 'auth' }
    );
  }

  const headers = new Headers(init.headers);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const response = await fetch(resolveUrl(pathOrUrl), {
    ...init,
    signal: session.signal,
    headers
  });

  check();
  if (response.ok) {
    if (response.status === 204) {
      return undefined as T;
    }
    const result = (await response.json()) as T;
    check();
    return result;
  }

  let graphCode: string | undefined;
  let message = `Microsoft Graph request failed (${response.status})`;
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string };
    };
    if (body.error?.code) graphCode = body.error.code;
    if (body.error?.message) message = body.error.message;
  } catch {
    // keep default message
  }

  check();
  const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'));
  const code = classifyStatus(response.status, access);

  throw new GraphClientError(message, {
    status: response.status,
    code,
    graphCode,
    retryAfterMs
  });
}

/** Follow @odata.nextLink until exhausted; max pages guards runaway lists. */
export async function graphFetchAll<T>(
  pathOrUrl: string,
  options?: { maxPages?: number; token?: string | null }
): Promise<T[]> {
  const session = captureSession();
  const maxPages = options?.maxPages ?? 50;
  const items: T[] = [];
  let next: string | null = pathOrUrl;
  let page = 0;

  while (next) {
    session.assertCurrent();
    page += 1;
    if (page > maxPages) {
      throw new GraphClientError(
        `Stopped after ${maxPages} pages of Microsoft Graph results.`,
        { status: 0, code: 'unknown' }
      );
    }
    const data: GraphODataCollection<T> = await graphFetch<GraphODataCollection<T>>(
      next,
      {},
      options?.token,
      'places'
    );
    session.assertCurrent();
    if (!Array.isArray(data.value)) {
      throw new GraphClientError('Microsoft Graph returned an invalid Places page.', {
        status: 0,
        code: 'invalid_response'
      });
    }
    items.push(...data.value);
    if (items.length > 5000) {
      throw new GraphClientError('Places discovery exceeded 5000 results.', {
        status: 0,
        code: 'validation'
      });
    }
    next = data['@odata.nextLink'] ?? null;
  }

  return items;
}

export function listGraphRoomLists(): Promise<GraphRoomList[]> {
  return graphFetchAll<GraphRoomList>('/places/microsoft.graph.roomlist?$top=100');
}

export function listGraphRooms(roomListEmailOrId?: string | null): Promise<GraphRoom[]> {
  if (roomListEmailOrId && roomListEmailOrId !== 'all') {
    const encoded = encodeURIComponent(roomListEmailOrId);
    return graphFetchAll<GraphRoom>(`/places/${encoded}/microsoft.graph.roomlist/rooms?$top=100`);
  }
  return graphFetchAll<GraphRoom>('/places/microsoft.graph.room?$top=100');
}

/** Free/busy statuses treated as busy for MVP (integrations §3.4). */
export const BUSY_SCHEDULE_STATUSES = [
  'busy',
  'oof',
  'workingElsewhere',
  'tentative'
] as const;

export type BusyScheduleStatus = (typeof BUSY_SCHEDULE_STATUSES)[number];

export interface GraphDateTimeTimeZone {
  dateTime: string;
  timeZone: string;
}

export interface GraphScheduleItem {
  status?: string | null;
  start?: GraphDateTimeTimeZone | null;
  end?: GraphDateTimeTimeZone | null;
}

export interface GraphScheduleInformation {
  scheduleId?: string | null;
  availabilityView?: string | null;
  scheduleItems?: GraphScheduleItem[] | null;
  error?: { message?: string; responseCode?: string } | null;
}

export interface GraphGetScheduleResponse {
  value?: GraphScheduleInformation[];
}

export interface BusyInterval {
  start: Date;
  end: Date;
  status: BusyScheduleStatus;
}

export interface RoomScheduleResult {
  mailbox: string;
  windowStart: Date;
  windowEnd: Date;
  observedAt: Date;
  busy: BusyInterval[];
  availabilityView?: string;
  source?: 'live' | 'mirror' | 'unconfirmed';
  mirror?: import('@cdf/api-client-react').IncomingRoomMirror;
}

function isBusyStatus(status: string | null | undefined): status is BusyScheduleStatus {
  if (!status) return false;
  return (BUSY_SCHEDULE_STATUSES as readonly string[]).includes(status);
}

/**
 * Parse Graph dateTime + timeZone into a Date.
 * When times include Z/offset, Date parses them as absolute instants.
 * For offset-less values, only UTC is accepted. Schedule requests explicitly
 * ask Graph for UTC, so a foreign or malformed timezone fails closed instead
 * of being interpreted as the browser's local clock.
 */
export function parseGraphDateTime(value: GraphDateTimeTimeZone | null | undefined): Date | null {
  if (!value?.dateTime) return null;
  const raw = value.dateTime.trim();
  // Already has offset or Z → Date parses correctly.
  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Treat only explicit UTC labels as UTC; never guess a foreign zone.
  const zone = (value.timeZone ?? '').toLowerCase();
  if (zone === 'utc' || zone === 'etc/utc' || zone === 'gmt' || zone === 'greenwich standard time') {
    const d = new Date(raw.endsWith('Z') ? raw : `${raw}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function validateWindow(start: Date, end: Date): void {
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
    throw new GraphClientError('Schedule window must have valid start and end instants.', {
      status: 0, code: 'validation'
    });
  }
  if (end.getTime() - start.getTime() >= 62 * 24 * 60 * 60_000) {
    throw new GraphClientError('Schedule window must be shorter than 62 days.', {
      status: 0, code: 'validation'
    });
  }
}

function invalidSchedule(message: string): GraphClientError {
  return new GraphClientError(message, { status: 0, code: 'invalid_response' });
}

function decodeSchedule(
  mailbox: string,
  info: GraphScheduleInformation | undefined,
  start: Date,
  end: Date,
  intervalMinutes: 15 | 30,
  observedAt: Date
): RoomScheduleResult | GraphClientError {
  if (!info || info.scheduleId?.trim().toLowerCase() !== mailbox) {
    return invalidSchedule('Microsoft Graph omitted the requested room schedule.');
  }
  if (info.error) {
    const graphCode = info.error.responseCode;
    const code = /access|denied|permission/i.test(graphCode ?? '') ? 'permission'
      : /notfound|not_found/i.test(graphCode ?? '') ? 'not_found' : 'unknown';
    return new GraphClientError('Microsoft Graph could not read this room schedule.', {
      status: code === 'permission' ? 403 : code === 'not_found' ? 404 : 400,
      code,
      graphCode
    });
  }
  if ((!Array.isArray(info.scheduleItems) || info.scheduleItems.length === 0)
      && typeof info.availabilityView !== 'string') {
    return invalidSchedule('Microsoft Graph returned no availability for this room.');
  }

  const busy: BusyInterval[] = [];
  if (Array.isArray(info.scheduleItems) && info.scheduleItems.length > 0) {
    for (const item of info.scheduleItems) {
      if (item.status === 'free') continue;
      if (!isBusyStatus(item.status)) return invalidSchedule('Microsoft Graph returned an unknown availability state.');
      const itemStart = parseGraphDateTime(item.start);
      const itemEnd = parseGraphDateTime(item.end);
      if (!itemStart || !itemEnd || itemEnd <= itemStart) {
        return invalidSchedule('Microsoft Graph returned an invalid busy interval.');
      }
      if (itemStart < end && itemEnd > start) {
        busy.push({
          start: new Date(Math.max(start.getTime(), itemStart.getTime())),
          end: new Date(Math.min(end.getTime(), itemEnd.getTime())),
          status: item.status
        });
      }
    }
  } else if (typeof info.availabilityView === 'string') {
    const view = info.availabilityView;
    const slotMs = intervalMinutes * 60_000;
    if (view.length < Math.ceil((end.getTime() - start.getTime()) / slotMs) || /[^0123]/.test(view)) {
      return invalidSchedule('Microsoft Graph returned an incomplete availability view.');
    }
    for (let index = 0; index < view.length; index++) {
      const status = view[index];
      if (status === '0') continue;
      const slotStart = start.getTime() + index * slotMs;
      if (slotStart >= end.getTime()) break;
      busy.push({
        start: new Date(slotStart),
        end: new Date(Math.min(slotStart + slotMs, end.getTime())),
        status: status === '1' ? 'tentative' : status === '3' ? 'oof' : 'busy'
      });
    }
  }
  busy.sort((a, b) => a.start.getTime() - b.start.getTime());
  return { mailbox, windowStart: start, windowEnd: end, observedAt, busy,
    availabilityView: info.availabilityView ?? undefined };
}

/** Browser-only Graph getSchedule; requests at most 20 mailboxes per batch. */
export async function getRoomSchedules(params: {
  mailboxes: string[];
  start: Date;
  end: Date;
  availabilityViewInterval?: 15 | 30;
  token?: string | null;
}): Promise<Map<string, RoomScheduleResult | GraphClientError>> {
  const session = captureSession();
  validateWindow(params.start, params.end);
  const mailboxes = [...new Set(params.mailboxes.map((value) => value.trim().toLowerCase()))];
  if (!mailboxes.length || mailboxes.length > 1000 || mailboxes.some((value) => !value)) {
    throw new GraphClientError('Provide 1 to 1000 linked room mailboxes.', {
      status: 0, code: 'validation'
    });
  }
  const intervalMinutes = params.availabilityViewInterval ?? 30;
  const results = new Map<string, RoomScheduleResult | GraphClientError>();
  for (let offset = 0; offset < mailboxes.length; offset += 20) {
    session.assertCurrent();
    const batch = mailboxes.slice(offset, offset + 20);
    const response = await graphFetch<GraphGetScheduleResponse>(
      '/me/calendar/getSchedule',
      {
        method: 'POST',
        headers: { Prefer: 'outlook.timezone="UTC"' },
        body: JSON.stringify({
          schedules: batch,
          startTime: { dateTime: params.start.toISOString().replace(/Z$/, ''), timeZone: 'UTC' },
          endTime: { dateTime: params.end.toISOString().replace(/Z$/, ''), timeZone: 'UTC' },
          availabilityViewInterval: intervalMinutes
        })
      },
      params.token,
      'calendar'
    );
    session.assertCurrent();
    if (!Array.isArray(response.value)) {
      throw invalidSchedule('Microsoft Graph returned an invalid schedule batch.');
    }
    const byMailbox = new Map(response.value
      .filter((item) => typeof item.scheduleId === 'string')
      .map((item) => [item.scheduleId!.trim().toLowerCase(), item]));
    const observedAt = new Date();
    for (const mailbox of batch) {
      results.set(mailbox, decodeSchedule(
        mailbox, byMailbox.get(mailbox), params.start, params.end, intervalMinutes, observedAt
      ));
    }
  }
  return results;
}

/** Compatibility helper for the existing single-room calendar view. */
export async function getRoomSchedule(params: {
  mailbox: string;
  start: Date;
  end: Date;
  availabilityViewInterval?: 15 | 30;
  token?: string | null;
}): Promise<RoomScheduleResult> {
  const result = await getRoomSchedules({ ...params, mailboxes: [params.mailbox] });
  const item = result.get(params.mailbox.trim().toLowerCase());
  if (!item) throw invalidSchedule('Microsoft Graph omitted the requested room schedule.');
  if (item instanceof GraphClientError) throw item;
  return item;
}
